import React, { useState, useEffect, useRef, useCallback } from 'react';
import storyComponents from 'storyComponents';
import markdownToHtml from 'markdown';

const PAUSE = '~~pause~~';
const NAMES = new Set(Object.keys(storyComponents));

const pushHtml = (tokens, html) => {
  let j = 0;
  while (j < html.length) {
    if (html[j] === '<') {
      const gt = html.indexOf('>', j);
      const stop = gt === -1 ? html.length : gt + 1;
      tokens.push({ type: 'html', str: html.slice(j, stop) });
      j = stop;
    } else {
      tokens.push({ type: 'html', str: html[j] });
      j += 1;
    }
  }
};

// Parse the RAW source, splitting out ~~pause~~ and component tags before
// running markdown — so markdown never mangles tag attributes (e.g.
// underscores in a filename).
const tokenize = (raw) => {
  const tokens = [];
  let i = 0;
  let textStart = 0;
  const flushText = (end) => {
    if (end > textStart)
      pushHtml(tokens, markdownToHtml(raw.slice(textStart, end)));
  };
  while (i < raw.length) {
    if (raw.startsWith(PAUSE, i)) {
      // Flush text up to the marker, trimming horizontal whitespace directly
      // before it so the pause adds no space.
      let end = i;
      while (end > textStart && (raw[end - 1] === ' ' || raw[end - 1] === '\t'))
        end -= 1;
      flushText(end);
      tokens.push({ type: 'pause' });
      i += PAUSE.length;
      // Consume the rest of the marker's own line (trailing whitespace + one
      // newline) so it introduces no gap in the render.
      while (i < raw.length && (raw[i] === ' ' || raw[i] === '\t')) i += 1;
      if (raw[i] === '\r') i += 1;
      if (raw[i] === '\n') i += 1;
      textStart = i;
    } else if (raw[i] === '<') {
      const m = raw.slice(i).match(/^<\s*([A-Za-z][A-Za-z0-9]*)([^>]*)>/);
      if (m && NAMES.has(m[1])) {
        // Trim horizontal whitespace before the tag, then consume the rest of
        // its own line after it, so the tag (a fixed layer, not inline) leaves
        // no whitespace behind under pre-wrap.
        let end = i;
        while (end > textStart && (raw[end - 1] === ' ' || raw[end - 1] === '\t'))
          end -= 1;
        flushText(end);
        const props = {};
        const attr = /([\w-]+)\s*=\s*"([^"]*)"/g;
        let a;
        while ((a = attr.exec(m[0]))) props[a[1]] = a[2];
        tokens.push({ type: 'component', name: m[1], props });
        i += m[0].length;
        while (i < raw.length && (raw[i] === ' ' || raw[i] === '\t')) i += 1;
        if (raw[i] === '\r') i += 1;
        if (raw[i] === '\n') i += 1;
        textStart = i;
      } else {
        i += 1;
      }
    } else {
      i += 1;
    }
  }
  flushText(raw.length);
  return tokens;
};

const Typer = ({ html, delay = 50 }) => {
  const [tokens, setTokens] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  // ?type=off renders the whole page at once (no typing effect, no pauses).
  const noType =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('type') === 'off';

  useEffect(() => {
    const raw = typeof html === 'string' ? html : '';
    const t = tokenize(raw);
    setTokens(t);
    setIndex(noType ? t.length : 0);
    setPaused(false);
  }, [html, noType]);

  useEffect(() => {
    if (paused || index >= tokens.length) return undefined;
    const token = tokens[index];
    if (token.type === 'pause') {
      setPaused(true);
      return undefined;
    }
    const instant = token.type === 'component' || token.str[0] === '<';
    timer.current = setTimeout(
      () => setIndex((n) => n + 1),
      instant ? 0 : delay
    );
    return () => clearTimeout(timer.current);
  }, [index, paused, tokens, delay]);

  const stateRef = useRef({});
  stateRef.current = { paused, index, tokens };

  const advance = useCallback(() => {
    const s = stateRef.current;
    if (s.paused) {
      setPaused(false);
      setIndex((n) => n + 1);
    } else if (s.index < s.tokens.length) {
      const next = s.tokens.findIndex(
        (t, i) => i >= s.index && t.type === 'pause'
      );
      setIndex(next === -1 ? s.tokens.length : next);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;
      if (e.key === 'Enter') {
        advance();
      } else if (e.key === 'Escape') {
        let target = 0;
        for (let i = s.index - 1; i >= 0; i--) {
          if (s.tokens[i].type === 'pause') {
            target = i;
            break;
          }
        }
        setPaused(false);
        setIndex(target);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [advance]);

  // Tapping the text mid-playback (mobile) acts like Enter. stopPropagation
  // keeps the tap from bubbling to the body handler that raises the keyboard.
  const handleTap = useCallback(
    (e) => {
      e.stopPropagation();
      advance();
    },
    [advance]
  );

  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [index]);

  const nodes = [];
  let buf = '';
  let bufKey = 0;
  const flush = () => {
    if (buf) {
      nodes.push(
        <span key={`h${bufKey}`} dangerouslySetInnerHTML={{ __html: buf }} />
      );
      buf = '';
    }
  };
  tokens.slice(0, index).forEach((t, i) => {
    if (t.type === 'html') {
      if (!buf) bufKey = i;
      buf += t.str;
    } else if (t.type === 'component') {
      flush();
      const Comp = storyComponents[t.name];
      if (Comp) nodes.push(<Comp key={`c${i}`} {...t.props} />);
    }
  });
  const done = index >= tokens.length;
  // While actively typing, inject the cursor into the buffer so it sits inside
  // whatever element is currently open (e.g. a code block's inline-block) and
  // follows the char being typed instead of floating after the block. When
  // paused, use a separate node below so it keeps its click-to-resume handler.
  if (!done && !paused) {
    if (!buf) bufKey = index;
    buf += '<span class="type-cursor"></span>';
  }
  flush();

  return (
    <div
      ref={scrollRef}
      className="typer-scroll"
      onClick={done ? undefined : handleTap}
      style={{
        maxHeight: 'calc(100dvh - 8rem)',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
        lineHeight: '2.5rem',
        cursor: done ? undefined : 'pointer'
      }}
    >
      {nodes}
      {!done && paused && (
        <span
          className="type-cursor"
          onClick={handleTap}
          style={{ cursor: 'pointer' }}
        />
      )}
    </div>
  );
};

export default Typer;
